// src/store/index.js
import { configureStore } from '@reduxjs/toolkit';
import authReducer from './slices/authSlice';
import servicesTesterReducer from './slices/servicesTesterSlice';
import gameReducer from './slices/gameSlice';
import characterDraftReducer from './slices/characterDraftSlice';
import { checkAuthStatus } from './thunks/authThunks';
import { loadCharacters } from './slices/gameSlice';

// Middleware для логирования
const logger = (store) => (next) => (action) => {
  const result = next(action);

  if (
    action.type.includes('HealthCheck') ||
    action.type.includes('Loading') ||
    action.type.includes('game/') ||
    action.type.includes('characterDraft/')
  ) {
    console.log('🔴 ACTION:', action.type);
    if (action.payload && typeof action.payload === 'object') {
      console.log('   Payload keys:', Object.keys(action.payload));
    }
  }

  return result;
};

// Middleware для синхронизации с Kratos
const kratosSyncMiddleware = (store) => (next) => (action) => {
  if (action.type.includes('auth/')) {
    console.log('🔐 Auth Action:', action.type, action.payload);
  }
  return next(action);
};

// Middleware для автоматической проверки аутентификации
const authMiddleware = (store) => (next) => (action) => {
  const state = store.getState();
  if (
    action.type.includes('auth/') ||
    action.type.includes('servicesTester/') ||
    action.type.includes('game/') ||
    action.type.includes('characterDraft/')
  ) {
    console.log('🔐 Auth Middleware - Current state:', {
      isAuthenticated: state.auth.isAuthenticated,
      hasSessionCookie:
        typeof document !== 'undefined' &&
        document.cookie.includes('ory_kratos_session'),
      user: state.auth.user?.traits?.email,
    });
  }
  return next(action);
};

// Middleware для обработки ошибок аутентификации
const authErrorMiddleware = (store) => (next) => (action) => {
  if (action.type.includes('rejected')) {
    const has401Error =
      (action.payload &&
        typeof action.payload === 'string' &&
        action.payload.includes('401')) ||
      (action.error &&
        action.error.message &&
        action.error.message.includes('401'));

    if (has401Error) {
      console.log('🚨 Authentication error detected, clearing invalid session...');

      if (typeof document !== 'undefined') {
        document.cookie.split(';').forEach(function (c) {
          const cookie = c.trim();
          if (cookie.includes('ory_kratos_session')) {
            document.cookie = cookie
              .replace(/^ +/, '')
              .replace(
                /=.*/,
                '=;expires=' + new Date().toUTCString() + ';path=/'
              );
            console.log('🧹 Cleared invalid session cookie');
          }
        });
      }

      store.dispatch({ type: 'auth/resetAuth' });
      store.dispatch({ type: 'game/resetGame' });
      store.dispatch({ type: 'characterDraft/resetDraft' });
    }
  }

  return next(action);
};

// Middleware для синхронизации игры, черновика и аутентификации
const gameAuthMiddleware = (store) => (next) => (action) => {
  // При logout очищаем состояние игры и черновика
  if (
    action.type === 'auth/logout/fulfilled' ||
    action.type === 'auth/resetAuth'
  ) {
    console.log('🎮 Clearing game state and character draft on logout');
    store.dispatch({ type: 'game/resetGame' });
    store.dispatch({ type: 'characterDraft/resetDraft' });
  }

  // При успешной аутентификации загружаем персонажей
  if (
    action.type === 'auth/checkStatus/fulfilled' ||
    action.type === 'auth/login/fulfilled'
  ) {
    const state = store.getState();
    if (state.auth.isAuthenticated && state.auth.user?.id) {
      console.log('🎮 Loading characters after auth success');
      setTimeout(() => {
        store.dispatch(loadCharacters({ userId: state.auth.user.id }));
      }, 1000);
    }
  }

  return next(action);
};

export const store = configureStore({
  reducer: {
    auth: authReducer,
    servicesTester: servicesTesterReducer,
    game: gameReducer,
    characterDraft: characterDraftReducer,
  },
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware({
      serializableCheck: {
        ignoredActions: ['servicesTester/addLog'],
        ignoredPaths: ['servicesTester.logs'],
      },
    }).concat(
      logger,
      kratosSyncMiddleware,
      authMiddleware,
      authErrorMiddleware,
      gameAuthMiddleware
    ),
  devTools: process.env.NODE_ENV !== 'production',
});

// Улучшенная инициализация аутентификации с диагностикой
export const initializeAuth = async () => {
  console.log('🚀 Initializing authentication...');

  try {
    const cookies = document.cookie;
    const hasSessionCookie = cookies.includes('ory_kratos_session');
    const allCookies = cookies.split(';').map((c) => c.trim());

    console.log('🍪 Cookie diagnostics:', {
      hasSessionCookie,
      totalCookies: allCookies.length,
      cookies: allCookies,
    });

    if (hasSessionCookie) {
      console.log('🔍 Found session cookie, checking auth status...');

      const timeoutPromise = new Promise((_, reject) =>
        setTimeout(() => reject(new Error('Auth check timeout')), 10000)
      );

      const authPromise = store.dispatch(checkAuthStatus());

      const result = await Promise.race([authPromise, timeoutPromise]);

      if (result.type === 'auth/checkStatus/fulfilled') {
        console.log('✅ Auth initialization successful', {
          user: result.payload?.identity?.traits?.email,
          sessionId: result.payload?.id,
        });

        const userId = result.payload?.identity?.id;
        if (userId) {
          console.log('🎮 Loading characters for user:', userId);
          store.dispatch(loadCharacters({ userId }));
        }

        return true;
      } else {
        console.log('❌ Auth initialization failed - invalid session');

        document.cookie.split(';').forEach(function (c) {
          const cookie = c.trim();
          if (cookie.includes('ory_kratos_session')) {
            document.cookie = cookie
              .replace(/^ +/, '')
              .replace(
                /=.*/,
                '=;expires=' + new Date().toUTCString() + ';path=/'
              );
          }
        });

        return false;
      }
    } else {
      console.log('🔍 No session cookie found');
      return false;
    }
  } catch (error) {
    console.error('💥 Auth initialization error:', error);

    if (
      error.message.includes('timeout') ||
      error.message.includes('Failed to fetch')
    ) {
      console.log('🧹 Clearing potentially invalid cookies due to error...');
      document.cookie.split(';').forEach(function (c) {
        const cookie = c.trim();
        if (cookie.includes('ory_kratos_session')) {
          document.cookie = cookie
            .replace(/^ +/, '')
            .replace(
              /=.*/,
              '=;expires=' + new Date().toUTCString() + ';path=/'
            );
        }
      });
    }

    return false;
  }
};

// Функция для диагностики состояния аутентификации
export const diagnoseAuth = async () => {
  console.log('🔍 Running auth diagnostics...');

  const diagnostics = {
    cookies: {
      hasSessionCookie: document.cookie.includes('ory_kratos_session'),
      allCookies: document.cookie.split(';').map((c) => c.trim()),
    },
    reduxState: {
      isAuthenticated: store.getState().auth.isAuthenticated,
      user: store.getState().auth.user,
      authChecked: store.getState().auth.authChecked,
      game: {
        characters: store.getState().game.characters?.length || 0,
        selectedCharacter: store.getState().game.selectedCharacter,
      },
      characterDraft: {
        mode: store.getState().characterDraft.mode,
        activeStep: store.getState().characterDraft.activeStep,
        name: store.getState().characterDraft.draft?.name || '',
        hasBackground: !!store.getState().characterDraft.draft?.background,
        initialized: store.getState().characterDraft.initialized,
      },
    },
    kratosEndpoints: {},
  };

  try {
    const publicHealth = await fetch('http://localhost:4433/health/ready', {
      credentials: 'include',
    });
    diagnostics.kratosEndpoints.public = {
      status: publicHealth.status,
      ok: publicHealth.ok,
    };
  } catch (error) {
    diagnostics.kratosEndpoints.public = { error: error.message };
  }

  try {
    const whoami = await fetch('http://localhost:4433/sessions/whoami', {
      credentials: 'include',
    });
    diagnostics.kratosEndpoints.whoami = {
      status: whoami.status,
      ok: whoami.ok,
    };
  } catch (error) {
    diagnostics.kratosEndpoints.whoami = { error: error.message };
  }

  console.log('📊 Auth diagnostics:', diagnostics);
  return diagnostics;
};

// Инициализируем при старте с улучшенной обработкой ошибок
initializeAuth().then((success) => {
  console.log('🎯 Auth initialization completed:', success);

  if (!success) {
    console.log('⚠️ Auth initialization failed, running diagnostics...');
    diagnoseAuth().then((diagnostics) => {
      console.log('📋 Full auth diagnostics completed');

      if (
        diagnostics.cookies.hasSessionCookie &&
        !diagnostics.reduxState.isAuthenticated
      ) {
        console.log('🧹 Cleaning orphaned session cookies...');
        document.cookie.split(';').forEach(function (c) {
          const cookie = c.trim();
          if (cookie.includes('ory_kratos_session')) {
            document.cookie = cookie
              .replace(/^ +/, '')
              .replace(
                /=.*/,
                '=;expires=' + new Date().toUTCString() + ';path=/'
              );
          }
        });
      }
    });
  }
});

// Глобальные функции для отладки
if (typeof window !== 'undefined') {
  window.reduxStore = store;
  window.getReduxState = () => store.getState();
  window.checkAuth = () => store.dispatch(checkAuthStatus());
  window.diagnoseAuth = diagnoseAuth;

  window.loadGameCharacters = (params) => store.dispatch(loadCharacters(params));
  window.getGameState = () => store.getState().game;
  window.clearGameSelection = () => store.dispatch({ type: 'game/clearSelection' });

  // 🔧 Отладка черновика персонажа
  window.getCharacterDraft = () => store.getState().characterDraft;
  window.resetCharacterDraft = () => store.dispatch({ type: 'characterDraft/resetDraft' });

  window.forceLogout = () => {
    console.log('🚨 Force logout initiated...');

    document.cookie.split(';').forEach(function (c) {
      const cookie = c.trim();
      if (
        cookie.includes('ory_kratos_session') ||
        cookie.includes('csrf')
      ) {
        document.cookie = cookie
          .replace(/^ +/, '')
          .replace(
            /=.*/,
            '=;expires=' + new Date().toUTCString() + ';path=/'
          );
        console.log('🧹 Cleared:', cookie.split('=')[0]);
      }
    });

    store.dispatch({ type: 'auth/resetAuth' });
    store.dispatch({ type: 'game/resetGame' });
    store.dispatch({ type: 'characterDraft/resetDraft' });

    console.log('✅ Force logout completed, reloading page...');
    setTimeout(() => {
      window.location.reload();
    }, 1000);
  };

  window.testAuthEndpoints = async () => {
    console.log('🧪 Testing auth endpoints...');

    const endpoints = [
      { name: 'Kratos Public Health', url: 'http://localhost:4433/health/ready' },
      { name: 'Kratos Whoami', url: 'http://localhost:4433/sessions/whoami' },
      { name: 'Kratos Admin Health', url: 'http://localhost:4434/health/ready' },
      { name: 'Universe Gateway', url: 'http://localhost:4000/api/health' },
      { name: 'ImCon', url: 'http://localhost:4001/api/health' },
    ];

    for (const endpoint of endpoints) {
      try {
        const response = await fetch(endpoint.url, {
          credentials: 'include',
          headers: { Accept: 'application/json' },
        });
        console.log(
          `📍 ${endpoint.name}: ${response.status} ${response.statusText}`
        );

        if (response.ok) {
          const data = await response.text();
          console.log(`   Response: ${data.substring(0, 100)}...`);
        }
      } catch (error) {
        console.error(`❌ ${endpoint.name}:`, error.message);
      }
    }
  };
}

console.log(
  '🟢 Redux store configured with enhanced auth, game system and character draft'
);

export default store;
